import {
  Button,
  Dialog,
  DialogTrigger,
  Group,
  Input,
  Label,
  NumberField,
  Popover,
} from 'react-aria-components'

import PassengerSvg from '@/assets/flight/icons/passenger.svg?react'
import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './InnosearchFlightPassengerSelector.module.scss'

interface Props {
  numOfAdult: number
  numOfChildren: number
  numOfInfantsInSeat: number
  numOfInfantsOnLap: number
  setNumOfAdult: MemoizedCallbackOrDispatch<number>
  setNumOfChildren: MemoizedCallbackOrDispatch<number>
  setNumOfInfantsInSeat: MemoizedCallbackOrDispatch<number>
  setNumOfInfantsOnLap: MemoizedCallbackOrDispatch<number>
  errorMessage?: string
}

export default memo(function InnosearchFlightPassengerSelector(props: Props) {
  const totalPax =
    props.numOfAdult + props.numOfChildren + props.numOfInfantsInSeat + props.numOfInfantsOnLap

  return (
    <DialogTrigger>
      <Button className={clsx(style.Button, style.Light)}>
        <div className={style.Value}>
          <PassengerSvg />
          <span>
            {totalPax} passenger{totalPax > 1 ? 's ' : ' '}
            <VisuallyHidden>
              ({props.numOfAdult} adult, {props.numOfChildren} children, {props.numOfInfantsInSeat}{' '}
              infant in seat, {props.numOfInfantsOnLap} infant on lap)
            </VisuallyHidden>
          </span>
        </div>
        <span aria-hidden className={style.Chevron}>
          <ChevronDownSvg
            className={style.ChevronIcon}
            width={12}
            height={16}
            viewBox='0 0 24 24'
            aria-hidden
          />
        </span>
      </Button>
      <Popover className={style.Popover}>
        <Dialog className={style.Dialog}>
          <ul>
            <li>
              <NumberField
                className={style.NumberField}
                value={props.numOfAdult}
                onChange={props.setNumOfAdult}
                minValue={1}
              >
                <Label>Adult</Label>
                <Group className={style.Group}>
                  <Button slot='decrement'>-</Button>
                  <Input />
                  <Button slot='increment'>+</Button>
                </Group>
              </NumberField>
            </li>
            <li>
              <NumberField
                className={style.NumberField}
                value={props.numOfChildren}
                onChange={props.setNumOfChildren}
                minValue={0}
              >
                <Label>Children (Aged 2-11)</Label>
                <Group className={style.Group}>
                  <Button slot='decrement'>-</Button>
                  <Input />
                  <Button slot='increment'>+</Button>
                </Group>
              </NumberField>
            </li>
            <li>
              <NumberField
                className={style.NumberField}
                value={props.numOfInfantsInSeat}
                onChange={props.setNumOfInfantsInSeat}
                minValue={0}
              >
                <Label>Infant in seat (Aged 0-2)</Label>
                <Group className={style.Group}>
                  <Button slot='decrement'>-</Button>
                  <Input />
                  <Button slot='increment'>+</Button>
                </Group>
              </NumberField>
            </li>
            <li>
              <NumberField
                className={style.NumberField}
                value={props.numOfInfantsOnLap}
                onChange={props.setNumOfInfantsOnLap}
                minValue={0}
              >
                <Label>Infant on lap (Aged 0-2)</Label>
                <Group className={style.Group}>
                  <Button slot='decrement'>-</Button>
                  <Input />
                  <Button slot='increment'>+</Button>
                </Group>
              </NumberField>
            </li>
          </ul>
          <div role='alert' className={style.ErrorMessage}>
            {props.errorMessage}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
})
